import { Container, Graphics } from 'pixi.js';
import { ITEM_TYPES, type ItemConfig } from '../configs';
export class Item extends Container {
  public lane: number;
  public type: string;
  private sprite: Graphics;
  private halo: Graphics;
  public active: boolean = true;
  private time: number = 0;
  constructor(lane: number, type: keyof typeof ITEM_TYPES) {
    super();
    this.lane = lane;
    this.type = type;
    this.sprite = this.createSprite(ITEM_TYPES[type]);
    this.halo = this.createHalo(ITEM_TYPES[type]);
    this.addChild(this.halo);
    this.addChild(this.sprite);
  }
  private createHalo(config: ItemConfig): Graphics {
    const halo = new Graphics();
    halo.fill({ color: config.color, alpha: 0.22 });
    halo.circle(0, 0, config.width / 2 + 10);
    halo.fill();
    return halo;
  }
  private createSprite(config: ItemConfig): Graphics {
    const graphic = new Graphics();
    // Main rounded body
    graphic.fill(config.color);
    graphic.roundRect(-config.width / 2, -config.height / 2, config.width, config.height, 10);
    graphic.fill();
    // Inner highlight border
    graphic.stroke({ width: 2, color: 0xffffff, alpha: 0.55 });
    graphic.roundRect(-config.width / 2 + 3, -config.height / 2 + 3, config.width - 6, config.height - 6, 8);
    graphic.stroke();
    // Icon based on type
    if (config.type === 'speed') {
      // Lightning bolt
      graphic.fill(0xffffff);
      graphic.moveTo(4, -14);
      graphic.lineTo(-7, 2);
      graphic.lineTo(-1, 2);
      graphic.lineTo(-4, 14);
      graphic.lineTo(7, -2);
      graphic.lineTo(1, -2);
      graphic.closePath();
      graphic.fill();
    } else if (config.type === 'fly') {
      // Wing shape
      graphic.fill(0xffffff);
      graphic.moveTo(-12, 6);
      graphic.lineTo(12, -10);
      graphic.lineTo(6, 0);
      graphic.lineTo(12, 8);
      graphic.closePath();
      graphic.fill();
      // Small feather line
      graphic.stroke({ width: 1.5, color: 0xffffff, alpha: 0.7 });
      graphic.moveTo(-6, 4);
      graphic.lineTo(6, -2);
      graphic.stroke();
    } else if (config.type === 'shield') {
      // Shield outline
      graphic.fill(0xffffff);
      graphic.moveTo(0, -12);
      graphic.lineTo(11, -5);
      graphic.lineTo(11, 4);
      graphic.lineTo(0, 12);
      graphic.lineTo(-11, 4);
      graphic.lineTo(-11, -5);
      graphic.closePath();
      graphic.fill();
      // Inner accent
      graphic.fill(0xfde047);
      graphic.moveTo(0, -7);
      graphic.lineTo(6, -3);
      graphic.lineTo(6, 2);
      graphic.lineTo(0, 7);
      graphic.lineTo(-6, 2);
      graphic.lineTo(-6, -3);
      graphic.closePath();
      graphic.fill();
    } else {
      // Star burst for score
      graphic.fill(0xffffff);
      for (let i = 0; i < 5; i++) {
        const outerAngle = ((i * 72) - 90) * Math.PI / 180;
        const x = Math.cos(outerAngle) * 10;
        const y = Math.sin(outerAngle) * 10;
        if (i === 0) {
          graphic.moveTo(x, y);
        } else {
          graphic.lineTo(x, y);
        }
        const innerAngle = ((i * 72 + 36) - 90) * Math.PI / 180;
        const ix = Math.cos(innerAngle) * 5;
        const iy = Math.sin(innerAngle) * 5;
        graphic.lineTo(ix, iy);
      }
      graphic.closePath();
      graphic.fill();
    }
    return graphic;
  }
  update(delta: number): void {
    this.time += delta;
    const bob = Math.sin(this.time * 0.12) * 3;
    this.sprite.y = bob;
    this.halo.y = bob;
    this.sprite.rotation = Math.sin(this.time * 0.08) * 0.18;
    const haloScale = 1 + Math.sin(this.time * 0.15) * 0.12;
    this.halo.scale.set(haloScale);
    this.halo.alpha = 0.6 + Math.sin(this.time * 0.15) * 0.25;
  }
  getBounds(): { x: number; y: number; width: number; height: number } {
    const config = ITEM_TYPES[this.type as keyof typeof ITEM_TYPES];
    return {
      x: this.x - config.width / 2,
      y: this.y - config.height / 2,
      width: config.width,
      height: config.height,
    };
  }
}